import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { appService } from "../../App/app.service";
import { getDefaultSalesType } from "../../util/customFunctions";

/**
 * useWonAcvData.js
 *
 * Custom hooks for fetching Won ACV mix data by dimension.
 * fetch filters, build defaults, dispatch Redux, fetch data, and
 * return a stable interface for cards.
 *
 * Returned interface:
 * - chartData: stacked bar data normalized by quarter and categories
 * - derivedCategories: sorted categories for legend/stack keys
 * - appliedFilters: filters applied to the analysis request
 * - isLoading: loading state derived from Redux and local fetch guard
 * - error: last error message (string) if fetch failed
 *
 */
function useWonAcvDataByDimension(dimensionKey) {
  const dispatch = useDispatch();
  const company = useSelector((state) => state.app.company);
  const apiResponse = useSelector(
    (state) => state.app.winAcvAnalysisData || ""
  );
  const defaultTimeFrame = useSelector(
    (state) => state.app.defaultTimeFrame || ""
  );

  const fetchingRef = useRef(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const isDataLoaded = Array.isArray(apiResponse) && apiResponse.length > 0;

  useEffect(() => {
    if (isDataLoaded || fetchingRef.current) return;
    fetchingRef.current = true;
    setIsLoading(true);
    setError("");

    dispatch({ type: "get_win_acv_filters_request" });

    appService.getWinACVMixAnalysisFilters().then(
      (resp) => {
        const lkpFilters = Object.values(
          resp.message.lkpFilters.reduce((r, c) => Object.assign(r, c), {})
        );
        const df = resp.message.defaultTimeFrame;
        const computedFilters = resp.message.computedFilters;

        dispatch({
          type: "get_win_acv_mix_filters_success",
          json: {
            ...resp,
            message: computedFilters,
            lkpFilters,
            defaultTimeFrame: df,
          },
        });

        dispatch({ type: "get_win_acv_mix_analysis_data_request" });

        const filters = [];
        const tfs = getDefaultSalesType(
          computedFilters.filter((i) => i.type === df),
          df
        );
        if (tfs[0] !== "All") {
          filters.push({ name: df, value: tfs, string: true });
        }

        const defaultOppType = getDefaultSalesType(
          computedFilters.filter((i) => i.type === "Type"),
          "Type"
        );
        const defaultSolutionCat = getDefaultSalesType(
          computedFilters.filter((i) => i.type === "Solution_Cat"),
          "Solution_Cat"
        );

        if (company === "C0008") {
          const salesFilters = getDefaultSalesType(
            computedFilters.filter((s) => s.type === "cross_sell1__c"),
            "cross_sell1__c"
          );
          const dealsFilters = getDefaultSalesType(
            computedFilters.filter((s) => s.type === "Type"),
            "Type"
          );
          if (salesFilters[0] !== "All")
            filters.push({ name: "cross_sell1__c", value: salesFilters });
          if (dealsFilters[0] !== "All")
            filters.push({ name: "Type", value: dealsFilters });
        }

        if (defaultOppType[0] !== "All")
          filters.push({ name: "Type", value: defaultOppType });
        if (defaultSolutionCat[0] !== "All")
          filters.push({ name: "Solution_Cat", value: defaultSolutionCat });

        setAppliedFilters(filters);
        dispatch({ type: "current_selected_filters", filters });

        appService
          .getWinAcvMixAnalysisData(
            df,
            computedFilters.length === 0 ? [] : filters
          )
          .then(
            (dataResp) => {
              dispatch({
                type: "get_win_acv_mix_analysis_data_success",
                json: dataResp,
              });
              fetchingRef.current = false;
              setIsLoading(false);
            },
            (err) => {
              const msg =
                typeof err === "object" ? "Something went wrong" : err;
              dispatch({
                type: "get_win_acv_mix_analysis_data_failure",
                error: msg,
              });
              setError(msg);
              fetchingRef.current = false;
              setIsLoading(false);
            }
          );
      },
      (err) => {
        const msg = typeof err === "object" ? "Something went wrong" : err;
        dispatch({ type: "get_win_acv_mix_filters_failure", error: msg });
        setError(msg);
        fetchingRef.current = false;
        setIsLoading(false);
      }
    );
  }, [company, dispatch, isDataLoaded]);

  /**
   * Transform Redux response into stacked bar data:
   * - Sort categories by total ACV (descending)
   * - Normalize per quarter: ensure every quarter has all categories
   */
  const { chartData, derivedCategories } = useMemo(() => {
    if (!isDataLoaded) return { chartData: [], derivedCategories: [] };

    const sourceDataObj = apiResponse.find(
      (item) =>
        item.type === dimensionKey ||
        (item.data &&
          item.data.length > 0 &&
          item.data[0][dimensionKey] !== undefined)
    );
    if (!sourceDataObj || !sourceDataObj.data)
      return { chartData: [], derivedCategories: [] };

    const rawData = sourceDataObj.data;
    const quarterField = sourceDataObj.timeline || "closed_fiscal_quarter";

    const totals = {};
    rawData.forEach((item) => {
      const cat = item[dimensionKey];
      if (cat) totals[cat] = (totals[cat] || 0) + (item.acv || 0);
    });
    const categories = Object.keys(totals).sort(
      (a, b) => totals[b] - totals[a]
    );

    const grouped = {};
    rawData.forEach((item) => {
      const quarter = item[quarterField];
      if (!quarter) return;
      if (!grouped[quarter]) {
        grouped[quarter] = { xAxisLabel: quarter };
        categories.forEach((c) => (grouped[quarter][c] = 0));
      }
      const key = item[dimensionKey];
      if (key) grouped[quarter][key] = item.acv || 0;
    });

    const transformed = Object.values(grouped).sort((a, b) =>
      (a.xAxisLabel || "").localeCompare(b.xAxisLabel || "")
    );

    return { chartData: transformed, derivedCategories: categories };
  }, [apiResponse, isDataLoaded, dimensionKey]);

  return { chartData, derivedCategories, appliedFilters, isLoading, error };
}

export function useWonAcvLeadSourceCategoryData() {
  return useWonAcvDataByDimension("Lead_Source_Category__c");
}

export function useWonAcvLeadSourceData() {
  return useWonAcvDataByDimension("Lead_Source");
}

export function useWonAcvIndustryData() {
  return useWonAcvDataByDimension("Acct_Industry");
}

export function useWonAcvCustomerSegmentData() {
  return useWonAcvDataByDimension("Acct_Size_Segment");
}

export function useWonAcvSalesTypeData() {
  return useWonAcvDataByDimension("cross_sell1__c");
}

export function useWonAcvCustomerTypeData() {
  return useWonAcvDataByDimension("Cust_Type");
}

export function useWonAcvOpportunityRecordTypeData() {
  return useWonAcvDataByDimension("Opportunity_Record_Type");
}

export function useWonAcvSolutionCategoryData() {
  return useWonAcvDataByDimension("Solution_Cat");
}
